/**
 * Enterprise Identity Store
 * 
 * Manages:
 * - Identity provider configurations
 * - Organizational hierarchy (departments, teams)
 * - User profiles with enterprise attributes
 * - Scoped role assignments
 * 
 * This store works alongside userStore but adds enterprise-specific
 * capabilities for departmental boundaries and SSO integration.
 * 
 * SUPABASE-ONLY PERSISTENCE:
 * - Primary: Supabase (single source of truth)
 * - No localStorage cache (eliminates stale data issues)
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type {
  IdentityProviderConfig,
  OrgUnit,
  OrgUnitMembership,
  ScopedActorRoleAssignment,
  EnterpriseUserProfile,
} from '../../types/prioritization/enterpriseIdentity';
import {
  fetchAll,
  insertRecord,
  updateRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  getSafeSupabaseClient,
  fromDbFormat,
  getCurrentWorkspaceId,
  type SyncState,
} from '../../lib/supabaseSync';
import { cascadeOrgUnitPathAndDepth } from '../../lib/orgUnitTree';

const ORG_UNITS_TABLE = 'org_units';

/** Scope for fetching org units: platform (all), tenant (workspace IDs in tenant), or workspace (single workspace) */
export interface OrgUnitsFetchScope {
  /** Single workspace: only org units in this workspace (or null) */
  workspaceId?: string | null;
  /** Multiple workspaces (e.g. tenant): org units in any of these workspaces */
  workspaceIds?: string[];
}

// ============================================================================
// State Interface
// ============================================================================


export interface OrgUnitWriteResult {
  ok: boolean;
  /** True when the change was kept locally only (no cloud client). */
  localOnly?: boolean;
  error?: string;
}

interface EnterpriseIdentityState {
  // Identity Providers
  identityProviders: IdentityProviderConfig[];
  activeProviderId: string | null;
  
  // Organizational Hierarchy
  orgUnits: Map<string, OrgUnit>;
  orgUnitTree: OrgUnit[];  // Root-level org units with children

  /** userId → orgUnitId. Populated from org_unit_memberships (or user_node_assignments) in fetchFromSupabase. */
  userNodeIndex: Map<string, string>;
  
  // Current User (enterprise profile)
  currentUserProfile: EnterpriseUserProfile | null;
  
  // Loading/Error State
  loading: boolean;
  error: string | null;
  
  // Supabase sync state
  supabaseSyncState: SyncState;
  
  // Supabase sync actions
  fetchFromSupabase: (scope?: OrgUnitsFetchScope) => Promise<void>;
  syncOrgUnitToSupabase: (orgUnit: OrgUnit) => Promise<{ success: boolean; error?: string }>;
  
  // ========================================================================
  // Identity Provider Actions
  // ========================================================================
  
  addIdentityProvider: (config: Omit<IdentityProviderConfig, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateIdentityProvider: (id: string, updates: Partial<IdentityProviderConfig>) => void;
  removeIdentityProvider: (id: string) => void;
  setActiveProvider: (id: string | null) => void;
  
  // ========================================================================
  // Org Unit Actions
  // ========================================================================
  
  addOrgUnit: (orgUnit: Omit<OrgUnit, 'id' | 'createdAt' | 'updatedAt'>) => string;
  /**
   * Optimistic, then awaited: resolves ok:false (with the server's reason)
   * after rolling the local change back when the write is refused.
   */
  updateOrgUnit: (id: string, updates: Partial<OrgUnit>) => Promise<OrgUnitWriteResult>;
  /** Retire (never delete). `reason` is recorded on the node (retired_reason). */
  removeOrgUnit: (id: string, reason?: string) => Promise<OrgUnitWriteResult>;
  
  // ========================================================================
  // User Profile Actions
  // ========================================================================
  
  setCurrentUserProfile: (profile: EnterpriseUserProfile) => void;
  updateCurrentUserProfile: (updates: Partial<EnterpriseUserProfile>) => void;
  addRoleAssignment: (assignment: Omit<ScopedActorRoleAssignment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  removeRoleAssignment: (assignmentId: string) => void;
  addOrgUnitMembership: (membership: Omit<OrgUnitMembership, 'id' | 'createdAt' | 'updatedAt'>) => void;
  removeOrgUnitMembership: (membershipId: string) => void;
  
  // ========================================================================
  // User Node Lookup (org-scoped Catchball)
  // ========================================================================

  /**
   * Returns the org unit node where a given user is directly placed.
   * Checks user_node_assignments via the loaded userNodeIndex.
   * Returns null if user has no direct placement or index is empty.
   *
   * When a user has multiple placements, this returns the shallowest
   * (see load sync). Prefer {@link getUserOrgUnitIds} for Catchball acting.
   */
  getUserOrgNode: (userId: string) => OrgUnit | null;

  /**
   * All org unit ids the user is actively assigned to (multi-placement).
   * Empty array when unknown / unassigned.
   */
  getUserOrgUnitIds: (userId: string) => string[];

  /** userId → every active org_unit_id assignment (Catchball multi-placement). */
  userOrgUnitIdsByUser: Map<string, string[]>;

  // ========================================================================
  // Utility
  // ========================================================================
  
  reset: () => void;
  
  // ========================================================================
  // Demo/Dev Data
  // ========================================================================
  
  loadDemoData: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

/** Stable empty — never allocate `[]` inside Zustand selectors / getUserOrgUnitIds. */
const EMPTY_USER_ORG_UNIT_IDS: string[] = [];

const INITIAL_STATE = {
  identityProviders: [],
  activeProviderId: null,
  orgUnits: new Map<string, OrgUnit>(),
  orgUnitTree: [],
  userNodeIndex: new Map<string, string>(),
  userOrgUnitIdsByUser: new Map<string, string[]>(),
  currentUserProfile: null,
  loading: false,
  error: null,
  supabaseSyncState: createInitialSyncState(),
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useEnterpriseIdentityStore = create<EnterpriseIdentityState>()(
  (set, get) => ({
    ...INITIAL_STATE,
      
      // ======================================================================
      // Supabase Sync Actions
      // ======================================================================
      
      fetchFromSupabase: async (scope?: OrgUnitsFetchScope) => {
        if (!isSupabaseAvailable()) {
          console.log('[EnterpriseIdentityStore] Supabase not available');
          return;
        }
        
        set({ supabaseSyncState: { ...get().supabaseSyncState, isLoading: true, syncError: null } });
        
        const supabase = getSafeSupabaseClient();
        let result: { data: OrgUnit[] | null; error: string | null };
        
        if (scope?.workspaceIds !== undefined && scope.workspaceIds.length > 0) {
          // Tenant scope: org units in any of these workspaces (or null workspace_id for legacy)
          const { data, error } = await supabase!
            .from(ORG_UNITS_TABLE)
            .select('*')
            .or(`workspace_id.in.(${scope.workspaceIds.join(',')}),workspace_id.is.null`)
            .order('depth', { ascending: true });
          result = {
            data: data ? (data as Record<string, unknown>[]).map((row: Record<string, unknown>) => fromDbFormat<OrgUnit>(row)) : null,
            error: error?.message ?? null,
          };
          console.log('[EnterpriseIdentityStore] Fetched org units scoped to tenant workspaces:', scope.workspaceIds.length, 'workspaces');
        } else if (scope?.workspaceIds !== undefined && scope.workspaceIds.length === 0) {
          // Tenant scope but no workspaces in tenant → show nothing (don't leak other orgs)
          result = { data: [], error: null };
          console.log('[EnterpriseIdentityStore] Fetched org units: tenant scope but no workspaces, returning empty');
        } else if (scope?.workspaceId !== undefined && scope.workspaceId !== null) {
          // Workspace scope: only this workspace (or null)
          const { data, error } = await supabase!
            .from(ORG_UNITS_TABLE)
            .select('*')
            .or(`workspace_id.eq.${scope.workspaceId},workspace_id.is.null`)
            .order('depth', { ascending: true });
          result = {
            data: data ? (data as Record<string, unknown>[]).map((row: Record<string, unknown>) => fromDbFormat<OrgUnit>(row)) : null,
            error: error?.message ?? null,
          };
          console.log('[EnterpriseIdentityStore] Fetched org units scoped to workspace:', scope.workspaceId);
        } else {
          // No scope: use current workspace when available so submit/queues see workspace-scoped queues
          const workspaceId = getCurrentWorkspaceId();
          if (workspaceId) {
            const { data, error } = await supabase!
              .from(ORG_UNITS_TABLE)
              .select('*')
              .or(`workspace_id.eq.${workspaceId},workspace_id.is.null`)
              .order('depth', { ascending: true });
            result = {
              data: data ? (data as Record<string, unknown>[]).map((row: Record<string, unknown>) => fromDbFormat<OrgUnit>(row)) : null,
              error: error?.message ?? null,
            };
            console.log('[EnterpriseIdentityStore] Fetched org units scoped to current workspace:', workspaceId);
          } else {
            result = await fetchAll<OrgUnit>(ORG_UNITS_TABLE, { column: 'depth', ascending: true });
          }
        }
        
        if (result.error) {
          set({ 
            supabaseSyncState: { 
              ...get().supabaseSyncState, 
              isLoading: false, 
              syncError: result.error 
            } 
          });
          console.error('[EnterpriseIdentityStore] Failed to fetch from Supabase:', result.error);
          return;
        }
        
        if (result.data && result.data.length > 0) {
          // Merge current in-memory data carefully so we do not drop queue settings while older rows are still normalized.
          const existingOrgUnits = get().orgUnits;
          const supabaseIds = new Set(result.data.map(ou => ou.id));
          
          // Start with Supabase data, but preserve in-memory queue settings when the fetched row omits them.
          const orgUnitsMap = new Map<string, OrgUnit>();
          result.data.forEach(ou => {
            const existingLocal = existingOrgUnits.get(ou.id);
            // Preserve queueSettings from the current in-memory row when the fetched row omits them.
            if (existingLocal?.queueSettings && !ou.queueSettings) {
              orgUnitsMap.set(ou.id, { ...ou, queueSettings: existingLocal.queueSettings });
            } else {
              orgUnitsMap.set(ou.id, ou);
            }
          });
          
          // Add local-only org units
          let localOnlyCount = 0;
          existingOrgUnits.forEach((ou, id) => {
            if (!supabaseIds.has(id)) {
              orgUnitsMap.set(id, ou);
              localOnlyCount++;
            }
          });

          // Build userNodeIndex (shallowest) + userOrgUnitIdsByUser (all placements)
          const userNodeIndex = new Map<string, string>();
          const userOrgUnitIdsByUser = new Map<string, string[]>();
          try {
            const orgUnitIds = Array.from(supabaseIds);
            if (orgUnitIds.length > 0) {
              const { data: assignments } = await supabase!
                .from('user_node_assignments')
                .select('user_id, org_unit_id')
                .in('org_unit_id', orgUnitIds)
                .eq('active', true);
              if (assignments && assignments.length > 0) {
                assignments.forEach((row: { user_id: string; org_unit_id: string }) => {
                  const uid = String(row.user_id);
                  const oid = String(row.org_unit_id);
                  const all = userOrgUnitIdsByUser.get(uid) ?? [];
                  if (!all.includes(oid)) {
                    all.push(oid);
                    userOrgUnitIdsByUser.set(uid, all);
                  }
                  const existing = userNodeIndex.get(uid);
                  if (!existing) {
                    userNodeIndex.set(uid, oid);
                  } else {
                    const existingOu = orgUnitsMap.get(existing);
                    const newOu = orgUnitsMap.get(oid);
                    if (newOu && existingOu && newOu.depth < existingOu.depth) {
                      userNodeIndex.set(uid, oid);
                    }
                  }
                });
              }
            }
          } catch {
            // Table may not exist; leave index empty
          }
          
          set({ 
            orgUnits: orgUnitsMap,
            orgUnitTree: rebuildOrgUnitTree(orgUnitsMap),
            userNodeIndex,
            userOrgUnitIdsByUser,
            supabaseSyncState: { 
              ...get().supabaseSyncState, 
              isLoading: false, 
              lastSyncAt: new Date(),
              syncError: null 
            }
          });
          console.log(`[EnterpriseIdentityStore] Loaded ${result.data.length} from Supabase, kept ${localOnlyCount} local-only. Total: ${orgUnitsMap.size}`);
        } else {
          // No Supabase data - keep existing org units
          set({ 
            supabaseSyncState: { 
              ...get().supabaseSyncState, 
              isLoading: false, 
              lastSyncAt: new Date(),
              syncError: null 
            }
          });
        }
      },
      
      syncOrgUnitToSupabase: async (orgUnit: OrgUnit) => {
        if (!isSupabaseAvailable()) {
          return { success: true };
        }
        
        set({ supabaseSyncState: { ...get().supabaseSyncState, isSyncing: true } });
        
        const result = await updateRecord<OrgUnit>(ORG_UNITS_TABLE, orgUnit.id, orgUnit);
        
        set({ supabaseSyncState: { ...get().supabaseSyncState, isSyncing: false } });
        
        if (result.error) {
          console.error('[EnterpriseIdentityStore] Failed to sync to Supabase:', result.error);
          return { success: false, error: result.error };
        }
        
        return { success: true };
      },
      
      // ======================================================================
      // Identity Provider Actions
      // ======================================================================
      
      addIdentityProvider: (config) => {
        const newProvider: IdentityProviderConfig = {
          ...config,
          id: generateUUID(),
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        set((state) => ({
          identityProviders: [...state.identityProviders, newProvider],
        }));
      },
      
      updateIdentityProvider: (id, updates) => {
        set((state) => ({
          identityProviders: state.identityProviders.map(p =>
            p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p
          ),
        }));
      },
      
      removeIdentityProvider: (id) => {
        set((state) => ({
          identityProviders: state.identityProviders.filter(p => p.id !== id),
          activeProviderId: state.activeProviderId === id ? null : state.activeProviderId,
        }));
      },
      
      setActiveProvider: (id) => {
        set({ activeProviderId: id });
      },
      
      // ======================================================================
      // Org Unit Actions
      // ======================================================================
      
      addOrgUnit: (orgUnit) => {
        const id = generateUUID();
        const parent = orgUnit.parentId ? get().orgUnits.get(orgUnit.parentId) : undefined;
        const newOrgUnit: OrgUnit = {
          ...orgUnit,
          id,
          path: parent ? [...parent.path, id] : [id],
          depth: parent ? parent.depth + 1 : 0,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        // Update local state first (optimistic update)
        set((state) => {
          const newMap = new Map(state.orgUnits);
          newMap.set(id, newOrgUnit);
          return {
            orgUnits: newMap,
            orgUnitTree: rebuildOrgUnitTree(newMap),
          };
        });
        
        // Then sync to Supabase
        if (isSupabaseAvailable()) {
          insertRecord<OrgUnit>(ORG_UNITS_TABLE, newOrgUnit).then(result => {
            if (result.error) {
              console.error('[EnterpriseIdentityStore] Failed to insert org unit to Supabase:', result.error);
              // Notify user that cloud sync failed
              window.dispatchEvent(new CustomEvent('store-warning', { 
                detail: { 
                  store: 'enterpriseIdentity', 
                  action: 'addOrgUnit', 
                  message: `Saved locally. Cloud sync failed: ${result.error}`,
                  severity: 'warning'
                } 
              }));
            } else {
              // Notify user of success
              window.dispatchEvent(new CustomEvent('store-success', { 
                detail: { 
                  store: 'enterpriseIdentity', 
                  action: 'addOrgUnit', 
                  message: `Created: ${newOrgUnit.displayName}`,
                } 
              }));
            }
          });
        }
        
        return id;
      },
      
      updateOrgUnit: async (id, updates) => {
        const existing = get().orgUnits.get(id);
        if (!existing) {
          console.warn('[EnterpriseIdentityStore] updateOrgUnit: org unit not found:', id);
          // Dispatch a custom event for the UI to catch
          window.dispatchEvent(new CustomEvent('store-error', {
            detail: {
              store: 'enterpriseIdentity',
              action: 'updateOrgUnit',
              message: `Org unit not found: ${id}`,
              severity: 'error'
            }
          }));
          return { ok: false, error: `Org unit not found: ${id}` };
        }

        const updated = { ...existing, ...updates, updatedAt: new Date() };
        const parentChanged =
          'parentId' in updates && updates.parentId !== existing.parentId;

        // Update local state first (optimistic update). On reparent, cascade
        // path/depth so Design Paths and other depth consumers stay in sync.
        const previousMap = get().orgUnits;
        let appliedMap: Map<string, OrgUnit> = previousMap;
        let persisted = updated;
        set((state) => {
          let newMap = new Map(state.orgUnits);
          newMap.set(id, updated);
          if (parentChanged) {
            newMap = cascadeOrgUnitPathAndDepth(newMap, id);
            persisted = newMap.get(id) ?? updated;
          }
          appliedMap = newMap;
          return {
            orgUnits: newMap,
            orgUnitTree: rebuildOrgUnitTree(newMap),
          };
        });

        if (!isSupabaseAvailable()) {
          window.dispatchEvent(new CustomEvent('store-warning', {
            detail: {
              store: 'enterpriseIdentity',
              action: 'updateOrgUnit',
              message: `Saved locally only: ${persisted.displayName}`,
              severity: 'warning',
            },
          }));
          return { ok: true, localOnly: true };
        }

        // Persist the edited unit only; the database cascades a reparent.
        const supabaseFields: Partial<OrgUnit> = {
          name: persisted.name,
          displayName: persisted.displayName,
          type: persisted.type,
          parentId: persisted.parentId,
          // path/depth are derived by the database (org_units_before_write,
          // mig 727), which also re-paths every descendant on a move.
          description: persisted.description,
          active: persisted.active,
          updatedAt: persisted.updatedAt,
          ...(persisted.queueSettings != null && { queueSettings: persisted.queueSettings }),
        };
        let writeError: string | null = null;
        try {
          const result = await updateRecord<Partial<OrgUnit>>(ORG_UNITS_TABLE, persisted.id, supabaseFields);
          writeError = result.error ?? null;
        } catch (e) {
          writeError = e instanceof Error ? e.message : 'Update failed';
        }

        if (writeError) {
          console.warn('[EnterpriseIdentityStore] updateOrgUnit failed, rolling back:', writeError);
          // Roll back only the entries this update changed and nothing has
          // touched since, so a concurrent edit to another unit survives.
          set((state) => {
            const newMap = new Map(state.orgUnits);
            for (const [unitId, applied] of appliedMap) {
              const before = previousMap.get(unitId);
              if (applied === before) continue;
              if (newMap.get(unitId) !== applied) continue;
              if (before) newMap.set(unitId, before);
              else newMap.delete(unitId);
            }
            return { orgUnits: newMap, orgUnitTree: rebuildOrgUnitTree(newMap) };
          });
          window.dispatchEvent(new CustomEvent('store-error', {
            detail: {
              store: 'enterpriseIdentity',
              action: 'updateOrgUnit',
              message: `Could not save ${existing.displayName}: ${writeError}`,
              severity: 'error',
            },
          }));
          return { ok: false, error: writeError };
        }

        window.dispatchEvent(new CustomEvent('store-success', {
          detail: {
            store: 'enterpriseIdentity',
            action: 'updateOrgUnit',
            message: `Updated: ${persisted.displayName}`,
          },
        }));
        return { ok: true };
      },

      removeOrgUnit: async (id, reason) => {
        const orgUnit = get().orgUnits.get(id);
        if (!orgUnit) return { ok: false, error: `Org unit not found: ${id}` };
        const displayName = orgUnit.displayName || id;

        // Structure nodes are retired, never deleted: the node keeps its id,
        // its name and every reference to it (mig 727). Optimistically mark it
        // inactive; roll back if the server refuses.
        const applyLocal = (unit: OrgUnit) =>
          set((state) => {
            const newMap = new Map(state.orgUnits);
            newMap.set(id, unit);
            return { orgUnits: newMap, orgUnitTree: rebuildOrgUnitTree(newMap) };
          });
        applyLocal({ ...orgUnit, active: false, updatedAt: new Date() });

        const client = getSafeSupabaseClient();
        if (!isSupabaseAvailable() || !client) return { ok: true, localOnly: true };

        const trimmedReason = reason?.trim();
        let rpcError: string | null = null;
        try {
          const { error } = await client.rpc('retire_structure_node', {
            p_kind: 'org',
            p_node_id: id,
            ...(trimmedReason ? { p_reason: trimmedReason } : {}),
          });
          rpcError = error ? (error as { message: string }).message : null;
        } catch (e) {
          rpcError = e instanceof Error ? e.message : 'Retire failed';
        }

        if (rpcError) {
          console.error('[EnterpriseIdentityStore] Failed to retire org unit:', rpcError);
          applyLocal(orgUnit);
          window.dispatchEvent(new CustomEvent('store-error', {
            detail: {
              store: 'enterpriseIdentity',
              action: 'removeOrgUnit',
              message: `Could not retire ${displayName}: ${rpcError}`,
              severity: 'error',
            },
          }));
          return { ok: false, error: rpcError };
        }

        window.dispatchEvent(new CustomEvent('store-success', {
          detail: {
            store: 'enterpriseIdentity',
            action: 'removeOrgUnit',
            message: `Retired: ${displayName}`,
          },
        }));
        return { ok: true };
      },

      // ======================================================================
      // User Profile Actions
      // ======================================================================
      
      setCurrentUserProfile: (profile) => {
        set({ currentUserProfile: profile });
      },
      
      updateCurrentUserProfile: (updates) => {
        set((state) => ({
          currentUserProfile: state.currentUserProfile
            ? { ...state.currentUserProfile, ...updates, updatedAt: new Date() }
            : null,
        }));
      },
      
      addRoleAssignment: (assignment) => {
        const newAssignment: ScopedActorRoleAssignment = {
          ...assignment,
          id: generateUUID(),
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        set((state) => ({
          currentUserProfile: state.currentUserProfile
            ? {
                ...state.currentUserProfile,
                roleAssignments: [...state.currentUserProfile.roleAssignments, newAssignment],
              }
            : null,
        }));
      },
      
      removeRoleAssignment: (assignmentId) => {
        set((state) => ({
          currentUserProfile: state.currentUserProfile
            ? {
                ...state.currentUserProfile,
                roleAssignments: state.currentUserProfile.roleAssignments.filter(
                  a => a.id !== assignmentId
                ),
              }
            : null,
        }));
      },
      
      addOrgUnitMembership: (membership) => {
        const newMembership: OrgUnitMembership = {
          ...membership,
          id: `mem-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        set((state) => ({
          currentUserProfile: state.currentUserProfile
            ? {
                ...state.currentUserProfile,
                orgUnitMemberships: [...state.currentUserProfile.orgUnitMemberships, newMembership],
              }
            : null,
        }));
      },
      
      removeOrgUnitMembership: (membershipId) => {
        set((state) => ({
          currentUserProfile: state.currentUserProfile
            ? {
                ...state.currentUserProfile,
                orgUnitMemberships: state.currentUserProfile.orgUnitMemberships.filter(
                  m => m.id !== membershipId
                ),
              }
            : null,
        }));
      },
      
      // ======================================================================
      // User Node Lookup
      // ======================================================================
      
      getUserOrgNode: (userId: string): OrgUnit | null => {
        const orgUnitId = get().userNodeIndex.get(userId);
        if (!orgUnitId) return null;
        return get().orgUnits.get(orgUnitId) ?? null;
      },

      getUserOrgUnitIds: (userId: string): string[] => {
        return get().userOrgUnitIdsByUser.get(userId) ?? EMPTY_USER_ORG_UNIT_IDS;
      },

      // ======================================================================
      // Utility
      // ======================================================================
      
      reset: () => {
        set(INITIAL_STATE);
      },
      
      // ======================================================================
      // Demo/Dev Data
      // ======================================================================
      
      loadDemoData: () => {
        // Create demo org units
        const orgUnitsData: OrgUnit[] = [
          {
            id: 'org-company',
            name: 'acme',
            displayName: 'Acme Corporation',
            type: 'organization',
            path: ['org-company'],
            depth: 0,
            active: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: 'dept-product',
            name: 'product',
            displayName: 'Product Development',
            type: 'department',
            parentId: 'org-company',
            path: ['org-company', 'dept-product'],
            depth: 1,
            active: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: 'team-product-core',
            name: 'core-product',
            displayName: 'Core Product Team',
            type: 'team',
            parentId: 'dept-product',
            path: ['org-company', 'dept-product', 'team-product-core'],
            depth: 2,
            active: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: 'team-product-platform',
            name: 'platform',
            displayName: 'Platform Team',
            type: 'team',
            parentId: 'dept-product',
            path: ['org-company', 'dept-product', 'team-product-platform'],
            depth: 2,
            active: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: 'dept-it',
            name: 'it',
            displayName: 'Information Technology',
            type: 'department',
            parentId: 'org-company',
            path: ['org-company', 'dept-it'],
            depth: 1,
            active: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: 'team-it-infra',
            name: 'infrastructure',
            displayName: 'Infrastructure Team',
            type: 'team',
            parentId: 'dept-it',
            path: ['org-company', 'dept-it', 'team-it-infra'],
            depth: 2,
            active: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: 'dept-finance',
            name: 'finance',
            displayName: 'Finance',
            type: 'department',
            parentId: 'org-company',
            path: ['org-company', 'dept-finance'],
            depth: 1,
            active: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
        ];
        
        const orgUnitsMap = new Map<string, OrgUnit>();
        orgUnitsData.forEach(ou => orgUnitsMap.set(ou.id, ou));
        
        // Create demo user profile with scoped role assignments
        const demoProfile: EnterpriseUserProfile = {
          id: 'user-demo-1',
          email: 'demo@acme.com',
          displayName: 'Demo User',
          firstName: 'Demo',
          lastName: 'User',
          identityProvider: 'local',
          primaryOrgUnitId: 'team-product-core',
          department: 'Product Development',
          title: 'Product Manager',
          orgUnitMemberships: [
            {
              id: 'mem-1',
              userId: 'user-demo-1',
              orgUnitId: 'team-product-core',
              membershipType: 'member',
              isPrimary: true,
              createdAt: new Date(),
              updatedAt: new Date(),
            },
          ],
          roleAssignments: [
            // Steward in Product team (can shape items)
            {
              id: 'ra-1',
              subjectType: 'user',
              subjectId: 'user-demo-1',
              role: 'steward',
              scopeKind: 'group',
              scopeId: 'dept-product',
              scopePath: ['org-company', 'dept-product'],
              inheritable: true,
              inherited: false,
              grantedBy: 'system',
              grantSource: 'manual',
              active: true,
              createdAt: new Date(),
              updatedAt: new Date(),
            },
            // List Owner for Core Product team
            {
              id: 'ra-2',
              subjectType: 'user',
              subjectId: 'user-demo-1',
              role: 'list-owner',
              scopeKind: 'group',
              scopeId: 'team-product-core',
              scopePath: ['org-company', 'dept-product', 'team-product-core'],
              inheritable: true,
              inherited: false,
              grantedBy: 'system',
              grantSource: 'manual',
              active: true,
              createdAt: new Date(),
              updatedAt: new Date(),
            },
            // Strategist at organization level (view-only across all)
            {
              id: 'ra-3',
              subjectType: 'user',
              subjectId: 'user-demo-1',
              role: 'strategist',
              scopeKind: 'organization',
              scopeId: 'org-company',
              scopePath: ['org-company'],
              inheritable: true,
              inherited: false,
              grantedBy: 'system',
              grantSource: 'manual',
              active: true,
              createdAt: new Date(),
              updatedAt: new Date(),
            },
          ],
          active: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        // Preserve existing queueSettings when loading demo data
        const existingOrgUnits = get().orgUnits;
        existingOrgUnits.forEach((existing, id) => {
          if (existing.queueSettings && orgUnitsMap.has(id)) {
            const newOu = orgUnitsMap.get(id)!;
            orgUnitsMap.set(id, { ...newOu, queueSettings: existing.queueSettings });
          }
        });
        
        set({
        orgUnits: orgUnitsMap,
        orgUnitTree: rebuildOrgUnitTree(orgUnitsMap),
        currentUserProfile: demoProfile,
      });
    },
  })
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Rebuild the org unit tree from flat map
 */
function rebuildOrgUnitTree(orgUnits: Map<string, OrgUnit>): OrgUnit[] {
  const rootUnits: OrgUnit[] = [];
  
  orgUnits.forEach(ou => {
    if (!ou.parentId) {
      rootUnits.push(ou);
    }
  });
  
  return rootUnits.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

// ============================================================================
// Selector Hooks
// ============================================================================

// Pre-defined stable selectors to avoid creating new functions on each render
const selectOrgUnitsArray = (state: EnterpriseIdentityState) => 
  Array.from(state.orgUnits.values());

/**
 * Get all org units as a flat array
 * Uses shallow comparison to prevent infinite re-renders from array creation
 */
export const useOrgUnits = () => 
  useEnterpriseIdentityStore(useShallow(selectOrgUnitsArray));

/**
 * Get org unit tree (root units)
 */
export const useOrgUnitTree = () =>
  useEnterpriseIdentityStore((state) => state.orgUnitTree);

/**
 * Get current user's enterprise profile
 */
export const useEnterpriseUserProfile = () =>
  useEnterpriseIdentityStore((state) => state.currentUserProfile);

/**
 * Get identity providers
 * Uses shallow comparison to prevent infinite re-renders from array reference changes
 */
export const useIdentityProviders = () =>
  useEnterpriseIdentityStore(useShallow((state) => state.identityProviders));

